import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Check, X, User, LogOut, ChevronDown, ChevronUp,
  Car, Gavel, Home, DollarSign, FileText, BarChart3, Download,
  School, GraduationCap, Briefcase, Heart, Users, Building2,
  MapPin, Receipt, CreditCard, Lightbulb, Package, Leaf, Trash2 as TrashIcon, AlertCircle
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


// Service icon mapping with colors
const getServiceIcon = (serviceName) => {
  const name = serviceName.toLowerCase();
  
  const iconMap = {
    'dmv': { Icon: Car, color: 'bg-blue-100 text-blue-600' },
    'vehicle': { Icon: Car, color: 'bg-blue-100 text-blue-600' },
    'ticket': { Icon: Gavel, color: 'bg-amber-100 text-amber-600' },
    'citation': { Icon: Gavel, color: 'bg-amber-100 text-amber-600' },
    'court': { Icon: Gavel, color: 'bg-amber-100 text-amber-600' },
    'bail': { Icon: Gavel, color: 'bg-red-100 text-red-600' },
    'probation': { Icon: FileText, color: 'bg-red-100 text-red-600' },
    'recreation': { Icon: Leaf, color: 'bg-green-100 text-green-600' },
    'park': { Icon: Leaf, color: 'bg-green-100 text-green-600' },
    'garbage': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600' },
    'trash': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600' },
    'waste': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600' },
    'solid waste': { Icon: TrashIcon, color: 'bg-slate-100 text-slate-600' },
    'rent': { Icon: Home, color: 'bg-purple-100 text-purple-600' },
    'housing': { Icon: Home, color: 'bg-purple-100 text-purple-600' },
    'property': { Icon: Building2, color: 'bg-indigo-100 text-indigo-600' },
    'tax': { Icon: Receipt, color: 'bg-rose-100 text-rose-600' },
    'payment': { Icon: CreditCard, color: 'bg-emerald-100 text-emerald-600' },
    'permit': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600' },
    'license': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600' },
    'misc': { Icon: FileText, color: 'bg-cyan-100 text-cyan-600' },
    'school': { Icon: School, color: 'bg-yellow-100 text-yellow-600' },
    'education': { Icon: GraduationCap, color: 'bg-yellow-100 text-yellow-600' },
    'utilities': { Icon: Lightbulb, color: 'bg-orange-100 text-orange-600' },
    'utility': { Icon: Lightbulb, color: 'bg-orange-100 text-orange-600' },
    'child support': { Icon: Users, color: 'bg-pink-100 text-pink-600' },
    'donation': { Icon: Heart, color: 'bg-red-100 text-red-500' },
    'estore': { Icon: Package, color: 'bg-violet-100 text-violet-600' },
    'store': { Icon: Package, color: 'bg-violet-100 text-violet-600' },
    'business': { Icon: Briefcase, color: 'bg-teal-100 text-teal-600' },
    'records': { Icon: FileText, color: 'bg-slate-100 text-slate-600' },
  };
  
  for (const [key, config] of Object.entries(iconMap)) {
    if (name.includes(key)) {
      return config;
    }
  }
  
  return { Icon: DollarSign, color: 'bg-slate-100 text-slate-600' };
};

export default function ServicesManagement() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const [editingCollections, setEditingCollections] = useState({ id: null, collection_amounts: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {serviceId, serviceName}
  const [editingDistribution, setEditingDistribution] = useState(null);
  const [rawPercentValues, setRawPercentValues] = useState({}); // Store raw string values during typing
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      toast.info('Preparing CSV export...');
      const response = await api.get('/admin/services/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `GW_Services_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Services CSV downloaded');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };
  
  // Check user role for edit permissions
  const userRole = user?.role || '';
  const canEdit = ['cfo', 'admin'].includes(userRole.toLowerCase());


  useEffect(() => {
    loadUser();
    fetchServices();
  }, []);

  const loadUser = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (parsedUser.role !== 'admin' && parsedUser.role !== 'approver') {
        toast.error('Access denied. Admin or Approver role required.');
        navigate('/calculator');
      }
    } else {
      navigate('/login');
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/services');
      console.log('Services fetched:', response.data.services.length);
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin or Approver role required.');
        navigate('/calculator');
      } else {
        toast.error('Failed to fetch services');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.trim()) {
      toast.error('Service name cannot be empty');
      return;
    }

    try {
      await api.post('/admin/services', 
        { name: newService.trim(), active: true }
      );
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success('Service added successfully');
      setNewService('');
      setShowAddForm(false);
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(error.response?.data?.detail || 'Failed to add service');
    }
  };

  const handleUpdateService = async (serviceId, newName) => {
    try {
      await api.put(`/admin/services/${serviceId}`,
        { name: newName, active: true }
      );
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success('Service updated successfully');
      setEditingService(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleDeleteService = async (serviceId, serviceName) => {
    console.log('handleDeleteService called:', serviceId, serviceName);
    setDeleteConfirm({ serviceId, serviceName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const { serviceId, serviceName } = deleteConfirm;
    console.log('Delete confirmed, making API call...');
    
    try {
      await api.delete(`/admin/services/${serviceId}`);
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success(`${serviceName} deleted successfully`);
      console.log('Delete successful, fetching services...');
      setDeleteConfirm(null);
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
      setDeleteConfirm(null);
    }
  };

  const handleToggleService = async (serviceId, serviceName, currentStatus) => {
    try {
      const response = await api.patch(`/admin/services/${serviceId}/toggle`, {}
      );
      
      const action = response.data.active ? 'activated' : 'deactivated';
      toast.success(`${serviceName} ${action} successfully`);
      fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
      toast.error('Failed to toggle service status');
    }
  };

  const handleUpdateCollections = async (serviceId, collectionAmounts) => {
    try {
      const service = services.find(s => s.id === serviceId);
      
      await api.put(`/admin/services/${serviceId}`,
        { 
          name: service.name, 
          active: true,
          collection_amounts: collectionAmounts
        }
      );
      
      toast.success('Collection amounts updated successfully');
      setEditingCollections(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating collections:', error);
      toast.error('Failed to update collection amounts');
    }
  };

  const handleUpdateDistribution = async (serviceId, distributionProfile) => {
    try {
      const service = services.find(s => s.id === serviceId);
      
      await api.put(`/admin/services/${serviceId}`,
        { 
          name: service.name,
          code: service.code,
          active: true,
          distribution_profile: distributionProfile
        }
      );
      
      toast.success('Transaction distribution updated successfully');
      setEditingDistribution(null);
      setRawPercentValues({}); // Clear raw values after saving
      fetchServices();
    } catch (error) {
      console.error('Error updating distribution:', error);
      toast.error('Failed to update distribution');
    }
  };

  const handleDistributionChange = (rangeIndex, field, value) => {
    const newDistribution = [...editingDistribution.distribution_profile];
    
    if (field === 'pct') {
      // Store raw string value for display during editing
      setRawPercentValues(prev => ({ ...prev, [rangeIndex]: value }));
      
      // Parse and store the actual decimal value
      if (value === '' || value === '.') {
        newDistribution[rangeIndex][field] = 0;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Convert percentage back to decimal
          newDistribution[rangeIndex][field] = numValue / 100;
        }
      }
    } else {
      const numValue = parseFloat(value) || 0;
      newDistribution[rangeIndex][field] = numValue;
      
      // Auto-calculate midpoint when low or high changes
      if (field === 'low' || field === 'high') {
        const low = field === 'low' ? numValue : newDistribution[rangeIndex].low;
        const high = field === 'high' ? numValue : newDistribution[rangeIndex].high;
        newDistribution[rangeIndex].mid = (low + high) / 2;
      }
    }
    
    setEditingDistribution({
      ...editingDistribution,
      distribution_profile: newDistribution
    });
  };

  const handleCollectionPercentChange = (rangeIndex, value) => {
    const newCollections = [...editingCollections.collection_amounts];
    newCollections[rangeIndex].percent = parseFloat(value) || 0;
    setEditingCollections({
      ...editingCollections,
      collection_amounts: newCollections
    });
  };

  const handleCollectionRangeChange = (rangeIndex, field, value) => {
    const newCollections = [...editingCollections.collection_amounts];
    const numValue = parseFloat(value) || 0;
    newCollections[rangeIndex][field] = numValue;
    
    // Auto-calculate midpoint when low or high changes
    if (field === 'low' || field === 'high') {
      const low = field === 'low' ? numValue : newCollections[rangeIndex].low;
      const high = field === 'high' ? numValue : newCollections[rangeIndex].high;
      newCollections[rangeIndex].midpoint = (low + high) / 2;
    }
    
    // Auto-update range label
    const ca = newCollections[rangeIndex];
    if (ca.low <= 30 && ca.high <= 30) {
      newCollections[rangeIndex].range = `≤ $${ca.high}`;
    } else {
      newCollections[rangeIndex].range = `${ca.low}–${ca.high}`;
    }
    
    setEditingCollections({
      ...editingCollections,
      collection_amounts: newCollections
    });
  };

  const getTotalPercent = (distributionProfile) => {
    return (distributionProfile.reduce((sum, item) => sum + (item.pct || 0), 0) * 100).toFixed(2);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-bold text-slate-900">Services Management</h1>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="flex items-center space-x-3 px-4 py-2 bg-slate-100 rounded-full">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">{user.name}</span>
                  <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                    {user.role}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="inline-flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-full mx-auto px-6 py-12">
        <div className="card p-6 overflow-visible">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Service Types</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                disabled={exporting || loading}
                data-testid="export-services-csv-btn"
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium text-sm disabled:opacity-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
              {canEdit ? (
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Service</span>
                </button>
              ) : (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800 font-medium">View Only - CFO/Admin required to edit</span>
                </div>
              )}
            </div>
          </div>

          {/* Add Service Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="Enter service name..."
                  className="input-field flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddService()}
                />
                <button
                  onClick={handleAddService}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewService('');
                  }}
                  className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Services List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-slate-600">Loading services...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  All Services ({services.length})
                </h3>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    <span>Active ({services.filter(s => s.active).length})</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span>Inactive ({services.filter(s => !s.active).length})</span>
                  </div>
                </div>
              </div>
              
              {services.map((service) => (
                <div
                  key={service.id}
                  className="border-2 border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Service Header */}
                  <div className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors gap-4">
                    {editingService?.id === service.id ? (
                      <div className="flex-1 flex items-center space-x-2 min-w-0">
                        <input
                          type="text"
                          value={editingService.name}
                          onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                          className="input-field flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && handleUpdateService(service.id, editingService.name)}
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateService(service.id, editingService.name)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingService(null)}
                          className="px-3 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {/* Service Icon Badge */}
                          {(() => {
                            const { Icon, color } = getServiceIcon(service.name);
                            return (
                              <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${color}`}>
                                <Icon className="w-5 h-5" />
                              </div>
                            );
                          })()}
                          
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="font-medium text-slate-900 break-words">{service.name}</span>
                            {service.distribution_profile && (
                              <span className="text-xs text-slate-500">
                                {service.distribution_profile.length} ranges · {getTotalPercent(service.distribution_profile)}% total
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedService(expandedService === service.id ? null : service.id);
                            }}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Expand/Collapse"
                          >
                            {expandedService === service.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          {canEdit ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingService(service);
                                }}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="Edit Name"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              
                              {/* Toggle Switch */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleService(service.id, service.name, service.active);
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  service.active ? 'bg-green-600' : 'bg-gray-300'
                                }`}
                                title={service.active ? 'Active - Click to Deactivate' : 'Inactive - Click to Activate'}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    service.active ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              
                              {/* Delete Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteService(service.id, service.name);
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Permanently"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                              service.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {service.active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Transaction Distribution Table - EDITABLE */}
                  {expandedService === service.id && service.distribution_profile && Array.isArray(service.distribution_profile) && service.distribution_profile.length > 0 && (
                    <div className="p-6 bg-gradient-to-br from-indigo-50 via-white to-slate-50 border-t-2 border-indigo-200 mt-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-base font-bold text-slate-900 flex items-center">
                          <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
                          Transaction Distribution Data
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-semibold">
                            9 Ranges
                          </span>
                          {editingDistribution?.id === service.id ? (
                            <>
                              <button
                                onClick={() => handleUpdateDistribution(service.id, editingDistribution.distribution_profile)}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Save Changes
                              </button>
                              <button
                                onClick={() => {
                                  setEditingDistribution(null);
                                  setRawPercentValues({}); // Clear raw values when canceling
                                }}
                                className="px-4 py-2 text-sm bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 font-semibold flex items-center"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingDistribution({ id: service.id, distribution_profile: JSON.parse(JSON.stringify(service.distribution_profile)) });
                                setRawPercentValues({}); // Clear raw values when entering edit mode
                              }}
                              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold flex items-center"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Edit Distribution
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-slate-200">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                              <tr>
                                <th className="text-left py-3 px-4 font-bold text-sm">Range</th>
                                <th className="text-right py-3 px-4 font-bold text-sm">Low ($)</th>
                                <th className="text-right py-3 px-4 font-bold text-sm">Midpoint ($)</th>
                                <th className="text-right py-3 px-4 font-bold text-sm">High ($)</th>
                                <th className="text-right py-3 px-4 font-bold text-sm">Percent (%)</th>
                                <th className="text-center py-3 px-4 font-bold text-sm">Distribution</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {(editingDistribution?.id === service.id ? editingDistribution.distribution_profile : service.distribution_profile).map((range, idx) => (
                                <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-indigo-50 transition-colors`}>
                                  <td className="py-3 px-4 font-bold text-slate-900 whitespace-nowrap">
                                    ${range.low?.toLocaleString() || 0} - ${range.high?.toLocaleString() || 0}
                                  </td>
                                  <td className="text-right py-3 px-4">
                                    {editingDistribution?.id === service.id ? (
                                      <input
                                        type="number"
                                        value={range.low || 0}
                                        onChange={(e) => handleDistributionChange(idx, 'low', e.target.value)}
                                        className="w-24 px-2 py-1 text-sm border-2 border-indigo-300 rounded text-right font-mono focus:ring-2 focus:ring-indigo-500"
                                        step="1"
                                        min="0"
                                      />
                                    ) : (
                                      <span className="text-slate-700 font-mono text-sm">${range.low?.toLocaleString() || 0}</span>
                                    )}
                                  </td>
                                  <td className="text-right py-3 px-4">
                                    <span className="text-indigo-700 font-mono text-sm bg-indigo-50 px-2 py-1 rounded font-semibold">
                                      ${range.mid?.toLocaleString() || 0}
                                    </span>
                                  </td>
                                  <td className="text-right py-3 px-4">
                                    {editingDistribution?.id === service.id ? (
                                      <input
                                        type="number"
                                        value={range.high || 0}
                                        onChange={(e) => handleDistributionChange(idx, 'high', e.target.value)}
                                        className="w-24 px-2 py-1 text-sm border-2 border-indigo-300 rounded text-right font-mono focus:ring-2 focus:ring-indigo-500"
                                        step="1"
                                        min="0"
                                      />
                                    ) : (
                                      <span className="text-slate-700 font-mono text-sm">${range.high?.toLocaleString() || 0}</span>
                                    )}
                                  </td>
                                  <td className="text-right py-3 px-4">
                                    {editingDistribution?.id === service.id ? (
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={rawPercentValues[idx] !== undefined ? rawPercentValues[idx] : ((range.pct || 0) * 100).toFixed(2)}
                                        onChange={(e) => handleDistributionChange(idx, 'pct', e.target.value)}
                                        onBlur={() => {
                                          // Format the value on blur
                                          const numValue = parseFloat(rawPercentValues[idx]);
                                          if (!isNaN(numValue)) {
                                            setRawPercentValues(prev => ({ ...prev, [idx]: numValue.toFixed(2) }));
                                          }
                                        }}
                                        className="w-20 px-2 py-1 text-sm border-2 border-indigo-300 rounded text-right font-bold focus:ring-2 focus:ring-indigo-500"
                                        placeholder="0.00"
                                      />
                                    ) : (
                                      <span className="font-bold text-indigo-700 text-base">
                                        {((range.pct || 0) * 100).toFixed(2)}%
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center justify-center">
                                      <div className="w-full max-w-[200px] bg-slate-200 rounded-full h-4 overflow-hidden shadow-inner">
                                        <div 
                                          className="bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700 h-4 rounded-full transition-all duration-700 flex items-center justify-end pr-1"
                                          style={{ width: `${Math.min((range.pct || 0) * 100, 100)}%` }}
                                        >
                                          {(range.pct || 0) > 0.05 && (
                                            <span className="text-white text-xs font-bold">
                                              {((range.pct || 0) * 100).toFixed(0)}%
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gradient-to-r from-slate-100 to-slate-200 border-t-2 border-slate-300">
                              <tr>
                                <td colSpan="4" className="py-3 px-4 text-right font-bold text-slate-800 text-base">
                                  TOTAL:
                                </td>
                                <td className="py-3 px-4 text-right font-bold text-lg">
                                  {(() => {
                                    const distribution = editingDistribution?.id === service.id ? editingDistribution.distribution_profile : service.distribution_profile;
                                    const total = (distribution.reduce((sum, r) => sum + (r.pct || 0), 0) * 100);
                                    return (
                                      <span className={`${
                                        Math.abs(total - 100) < 1 ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {total.toFixed(1)}%
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="py-3 px-4 text-center text-xs text-slate-600">
                                  {(() => {
                                    const distribution = editingDistribution?.id === service.id ? editingDistribution.distribution_profile : service.distribution_profile;
                                    const total = (distribution.reduce((sum, r) => sum + (r.pct || 0), 0) * 100);
                                    return Math.abs(total - 100) < 1 ? '✓' : '⚠️';
                                  })()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-xs text-slate-500 text-center bg-slate-100 py-2 rounded">
                        <strong>Note:</strong> All percentages should total 100%. Midpoint is auto-calculated from Low and High values.
                      </div>
                    </div>
                  )}

                  {/* OLD Collection Amounts Editor - HIDDEN, replaced by distribution_profile above */}
                  {false && expandedService === service.id && service.collection_amounts && (
                    <div className="p-4 bg-slate-50 border-t border-slate-200">
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-slate-700">Collection Amount Ranges</h4>
                        {editingCollections?.id === service.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleUpdateCollections(service.id, editingCollections.collection_amounts)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={() => setEditingCollections(null)}
                              className="px-3 py-1 text-sm bg-slate-300 text-slate-700 rounded hover:bg-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingCollections({ id: service.id, collection_amounts: [...service.collection_amounts] })}
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          >
                            Edit Ranges & Percentages
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts).map((ca, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border border-slate-200">
                            {editingCollections?.id === service.id ? (
                              // Edit Mode - Full Range Editing
                              <div className="space-y-3">
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">Low ($)</label>
                                    <input
                                      type="number"
                                      value={ca.low}
                                      onChange={(e) => handleCollectionRangeChange(idx, 'low', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                                      step="1"
                                      min="0"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">High ($)</label>
                                    <input
                                      type="number"
                                      value={ca.high}
                                      onChange={(e) => handleCollectionRangeChange(idx, 'high', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                                      step="1"
                                      min="0"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">Midpoint ($)</label>
                                    <input
                                      type="number"
                                      value={ca.midpoint}
                                      onChange={(e) => handleCollectionRangeChange(idx, 'midpoint', e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded bg-slate-50"
                                      step="0.1"
                                      readOnly
                                      title="Auto-calculated from Low and High"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-600 font-medium">Percent (%)</label>
                                    <input
                                      type="number"
                                      value={ca.percent}
                                      onChange={(e) => handleCollectionPercentChange(idx, e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                    />
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500">
                                  Range: <span className="font-semibold">{ca.range}</span>
                                </div>
                              </div>
                            ) : (
                              // View Mode
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-slate-700">{ca.range}</div>
                                  <div className="text-xs text-slate-500 mt-1">
                                    ${ca.low} - ${ca.high} (Midpoint: ${ca.midpoint})
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm font-semibold text-slate-900 w-16 text-right">
                                    {ca.percent?.toFixed(2)}%

                  )}

                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Total:</span>
                          <span className={`text-sm font-bold ${
                            Math.abs(getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts) - 100) < 0.1
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts)}%
                          </span>
                        </div>
                        {Math.abs(getTotalPercent(editingCollections?.id === service.id ? editingCollections.collection_amounts : service.collection_amounts) - 100) >= 0.1 && (
                          <p className="text-xs text-red-600 mt-1">⚠ Total should equal 100%</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {services.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No services found. Click "Add Service" to create one.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to permanently delete <strong>"{deleteConfirm.serviceName}"</strong>?
              <br /><br />
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
